import { Err, Ok } from 'ts-results';
import type { Result } from 'ts-results';

export type Path = (string | number)[];

export interface ValidationCtx {
  path: Path;
}

/**
 * Visit object member, keeping track of path for validation error.
 */
export function withPath<T, K extends keyof T & (number | string), Ret>(
  ob: T,
  key: K,
  inner: (v: T[K]) => Ret
): Ret {
  if (currentCtx == null) {
    throw new Error('not in path context');
  }
  currentCtx.path.push(key);
  const res = inner(ob[key]);
  currentCtx.path.pop();
  return res;
}

/**
 * Call a function with given path appended to validation context.
 */
export function withExternalPath<Ret>(path: Path, inner: () => Ret): Ret {
  if (currentCtx == null) {
    throw new Error('not in path context');
  }
  const oldPath = currentCtx.path;
  currentCtx.path = [...oldPath, ...path];
  const res = inner();
  currentCtx.path = oldPath;
  return res;
}

/**
 * Visit each array member, keeping track of path for validation error.
 */
export function forEachPath<T>(arr: T[], inner: (v: T) => void) {
  if (currentCtx == null) {
    throw new Error('not in path context');
  }
  const idx = currentCtx.path.length;
  currentCtx.path.push(0);
  for (let i = 0; i < arr.length; i++) {
    currentCtx.path[idx] = i;
    inner(arr[i]);
  }
  currentCtx.path.pop();
}

/**
 * Throw an validation error with specified message.
 * Attaches the current validation path to the error context.
 */
export function bail(msg: string): never {
  if (currentCtx == null) {
    throw new Error('not in path context');
  }
  throw new ValidationError(currentCtx.path, msg);
}

export class ValidationError extends Error {
  constructor(
    protected path: Path,
    protected validationMessage: string
  ) {
    super(`[${path.join('.')}]: ${validationMessage}`);
  }
}

/**
 * Create a new validation context and immediately calls a function in it.
 *
 * If the validation performed inside the callback throws an validation error,
 * it is being returned as an error result. Any other exception is rethrown.
 *
 * This function is not reentrant. Creating a new context while another is
 * present will cause an immediate error.
 */
export function catchValidation<T>(f: () => T): Result<T, ValidationError> {
  if (currentCtx !== null) {
    throw new Error('Validation context already started');
  }
  currentCtx = { path: [] };
  try {
    return Ok(f());
  } catch (e) {
    if (e instanceof ValidationError) {
      return Err(e);
    }
    // any non-validation exceptions are not supposed to be catched here
    throw e;
  } finally {
    currentCtx = null;
  }
}

/** Module-local that holds currently opened validation context */
let currentCtx: ValidationCtx | null = null;
